import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { notification } from "antd";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import { Tooltip } from "antd";
import { cn } from "../../lib/utils";
import { usePermissions } from "../../PermissionsProvider";
import {
  WorkflowService,
  WorkflowTestRun,
  WorkflowTestRunItem,
  WorkflowTestSet,
} from "../../services/WorkflowService";
import moment from "moment";
import {
  CheckCircleIcon,
  XCircleIcon,
  AlertCircleIcon,
  LoaderIcon,
} from "lucide-react";
import WorkflowTestRunItemsTable from "./WorkflowTestRunItemsTable";
import WorkflowTestRunItemComparison from "./WorkflowTestRunItemComparison";

const ITEMS_PER_PAGE = 10;

const WorkflowTestRunDetail: React.FC = () => {
  const { testSetId, testRunId } = useParams<{ testSetId: string; testRunId: string }>();
  const { apiKey } = usePermissions();
  const navigate = useNavigate();

  const [testSet, setTestSet] = useState<WorkflowTestSet | null>(null);
  const [testRun, setTestRun] = useState<WorkflowTestRun | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [items, setItems] = useState<WorkflowTestRunItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPage, setItemsPage] = useState(1);
  const [isItemsLoading, setIsItemsLoading] = useState(false);
  const [matchedFilter, setMatchedFilter] = useState<boolean | null>(null);

  const [comparisonItem, setComparisonItem] = useState<WorkflowTestRunItem | null>(null);
  const [isComparisonVisible, setIsComparisonVisible] = useState(false);

  useEffect(() => {
    if (!testSetId) return;
    WorkflowService.getWorkflowTestSet(Number(testSetId), 0, 0, setIsLoading, apiKey).subscribe(
      (data: WorkflowTestSet) => setTestSet(data),
      (err: unknown) => console.error("Failed to fetch test set:", err)
    );
  }, [testSetId, apiKey]);

  useEffect(() => {
    if (!testRunId) return;
    setIsLoading(true);
    WorkflowService.getWorkflowTestRun(Number(testRunId), setIsLoading, apiKey).subscribe(
      (data: WorkflowTestRun) => {
        setTestRun(data);
        setIsLoading(false);
      },
      (err: unknown) => {
        notification.error({ message: "Error", description: `Failed to fetch test run: ${err}` });
        setIsLoading(false);
      }
    );
  }, [testRunId, apiKey]);

  const fetchItems = (page: number, filter: boolean | null) => {
    if (!testRunId) return;
    setIsItemsLoading(true);
    const offset = (page - 1) * ITEMS_PER_PAGE;
    WorkflowService.getWorkflowTestRunItems(
      Number(testRunId), offset, ITEMS_PER_PAGE, setIsItemsLoading, apiKey, filter
    ).subscribe(
      (data: { items: WorkflowTestRunItem[]; total: number }) => {
        setItems(data.items || []);
        setTotalItems(data.total || 0);
        setIsItemsLoading(false);
      },
      (err: unknown) => {
        notification.error({ message: "Error", description: `Failed to fetch items: ${err}` });
        setIsItemsLoading(false);
      }
    );
  };

  useEffect(() => {
    fetchItems(itemsPage, matchedFilter);
  }, [testRunId, itemsPage, matchedFilter, apiKey]);

  // Poll for progress when the run is in-progress
  useEffect(() => {
    if (!testRun || (testRun.status !== "RUNNING" && testRun.status !== "CREATED")) return;
    const interval = setInterval(() => {
      WorkflowService.getWorkflowTestRun(Number(testRunId), () => {}, apiKey).subscribe(
        (data: WorkflowTestRun) => {
          setTestRun(data);
          if (data.status !== "RUNNING" && data.status !== "CREATED") {
            fetchItems(itemsPage, matchedFilter);
          }
        },
        () => {}
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [testRun?.status, testRunId, apiKey]);

  const handleMatchedFilterChange = (val: boolean | null) => {
    setMatchedFilter(val);
    setItemsPage(1);
  };

  const handleCompare = (item: WorkflowTestRunItem) => {
    setComparisonItem(item);
    setIsComparisonVisible(true);
  };

  const isRunning = testRun?.status === "RUNNING" || testRun?.status === "CREATED";
  const passRate = testRun && (testRun.passed_count + testRun.failed_count) > 0
    ? Math.round((testRun.passed_count / (testRun.passed_count + testRun.failed_count)) * 100)
    : null;
  const completedPercent = testRun && testRun.item_count > 0
    ? Math.round(((testRun.completed_count + testRun.error_count) / testRun.item_count) * 100)
    : null;

  const avgFieldMatch = testRun?.avg_field_match_rate ?? null;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button
                    onClick={() => navigate("/test?tab=workflow")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Test Sets
                  </button>
                </li>
                <li className="text-muted-foreground/40">/</li>
                {testSet && (
                  <>
                    <li>
                      <button
                        onClick={() => navigate(`/test/workflow-test-sets/${testSetId}`)}
                        className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                      >
                        {testSet.test_set_name}
                      </button>
                    </li>
                    <li className="text-muted-foreground/40">/</li>
                    <li>
                      <button
                        onClick={() => navigate(`/test/workflow-test-sets/${testSetId}?tab=runs`)}
                        className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                      >
                        Test Runs
                      </button>
                    </li>
                    <li className="text-muted-foreground/40">/</li>
                  </>
                )}
                <li>
                  <span className="text-maitai-green font-gc-med">
                    Test Run #{testRunId}
                  </span>
                </li>
              </ol>
            </nav>
            <div className="space-y-1">
              {isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-64" />
                  <Skeleton className="h-5 w-96" />
                </div>
              ) : (
                <>
                  <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                    Test Run #{testRunId}
                  </h1>
                  <p className="text-lg text-muted-foreground font-gc-reg flex items-center gap-2">
                    {testSet?.test_set_name}
                    <Badge variant="secondary">Workflow-based</Badge>
                  </p>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Details Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <h2 className="text-xl font-gc-med text-foreground mb-6">Details</h2>
            {isLoading ? (
              <div className="grid md:grid-cols-3 gap-6">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-5 w-32" />
                  </div>
                ))}
              </div>
            ) : testRun ? (
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground font-gc-reg">Date Created</div>
                  <div className="text-sm font-gc-reg text-foreground">
                    {moment(testRun.date_created).format("MMM D, YYYY HH:mm:ss")}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground font-gc-reg">Date Completed</div>
                  <div className="text-sm font-gc-reg text-foreground">
                    {testRun.date_completed
                      ? moment(testRun.date_completed).format("MMM D, YYYY HH:mm:ss")
                      : "—"}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground font-gc-reg">Status</div>
                  <div>
                    <span className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium",
                      testRun.status === "COMPLETED" && "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400",
                      testRun.status === "ERROR" && "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400",
                      testRun.status === "RUNNING" && "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400",
                      testRun.status === "CREATED" && "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-400",
                    )}>
                      {isRunning && <LoaderIcon className="w-3 h-3 animate-spin" />}
                      {testRun.status}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground font-gc-reg">Workflow</div>
                  <div className="text-sm font-gc-reg text-foreground">
                    {testRun.workflow_name || "—"}
                  </div>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <div className="text-sm text-muted-foreground font-gc-reg">Description</div>
                  <div className="text-sm font-gc-reg text-foreground">
                    {testRun.description || "No description provided"}
                  </div>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {/* Results Summary Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <h2 className="text-xl font-gc-med text-foreground mb-6">Results</h2>
            {isLoading ? (
              <div className="grid md:grid-cols-5 gap-6">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                ))}
              </div>
            ) : testRun ? (
              <>
                <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground font-gc-reg"># Items</div>
                    <div className="text-sm font-gc-reg text-foreground">{testRun.item_count}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground font-gc-reg">Completed</div>
                    <div className="text-sm font-gc-reg text-foreground">
                      {completedPercent != null ? `${completedPercent}%` : "—"}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground font-gc-reg">Pass Rate</div>
                    <div className={cn(
                      "text-sm font-gc-med",
                      passRate !== null && passRate >= 80 ? "text-green-600 dark:text-green-400" :
                      passRate !== null && passRate >= 60 ? "text-yellow-600 dark:text-yellow-400" :
                      passRate !== null ? "text-red-600 dark:text-red-400" : "text-muted-foreground"
                    )}>
                      {passRate !== null ? `${passRate}%` : "—"}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground font-gc-reg">Avg Field Match</div>
                    <div className={cn(
                      "text-sm font-gc-med",
                      avgFieldMatch !== null && avgFieldMatch === 100 ? "text-green-600 dark:text-green-400" :
                      avgFieldMatch !== null && avgFieldMatch >= 80 ? "text-yellow-600 dark:text-yellow-400" :
                      avgFieldMatch !== null ? "text-red-600 dark:text-red-400" : "text-muted-foreground"
                    )}>
                      {avgFieldMatch !== null ? `${avgFieldMatch}%` : "—"}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground font-gc-reg">Error Rate</div>
                    <div className="text-sm font-gc-reg text-foreground">
                      {testRun.item_count > 0
                        ? `${Math.round((testRun.error_count / testRun.item_count) * 100)}%`
                        : "—"}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground font-gc-reg">Response Time</div>
                    <div className="text-sm font-gc-reg text-foreground">
                      {testRun.duration_ms?.p50 != null ? (
                        <Tooltip
                          title={
                            <div>
                              <div>P50: {testRun.duration_ms.p50}ms</div>
                              {testRun.duration_ms.p90 != null && (
                                <div>P90: {testRun.duration_ms.p90}ms</div>
                              )}
                              {testRun.duration_ms.p95 != null && (
                                <div>P95: {testRun.duration_ms.p95}ms</div>
                              )}
                            </div>
                          }
                        >
                          <span>{testRun.duration_ms.p50}ms</span>
                        </Tooltip>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                </div>

                {/* Pass/Fail/Error breakdown bar */}
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground font-gc-reg">Result Distribution</div>
                  {testRun.item_count > 0 ? (
                    <>
                      <div className="flex items-center gap-3 text-xs mb-2">
                        <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                          <CheckCircleIcon className="w-3 h-3" /> {testRun.passed_count} Passed
                        </span>
                        <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                          <XCircleIcon className="w-3 h-3" /> {testRun.failed_count} Failed
                        </span>
                        {testRun.error_count > 0 && (
                          <span className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
                            <AlertCircleIcon className="w-3 h-3" /> {testRun.error_count} Error
                          </span>
                        )}
                        {isRunning && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <LoaderIcon className="w-3 h-3 animate-spin" />
                            {testRun.item_count - testRun.completed_count - testRun.error_count} Pending
                          </span>
                        )}
                      </div>
                      <div className="w-full bg-muted rounded-full h-3 flex overflow-hidden">
                        {testRun.passed_count > 0 && (
                          <Tooltip title={`Passed: ${testRun.passed_count} (click to filter)`}>
                            <div
                              role="button"
                              tabIndex={0}
                              className="bg-green-500 h-full transition-all cursor-pointer hover:opacity-90"
                              style={{
                                width: `${(testRun.passed_count / testRun.item_count) * 100}%`,
                                opacity: matchedFilter === null || matchedFilter === true ? 1 : 0.5,
                                boxShadow: matchedFilter === true ? "0 0 0 2px #1890ff" : "none",
                              }}
                              onClick={() => handleMatchedFilterChange(matchedFilter === true ? null : true)}
                              onKeyDown={(e) => e.key === "Enter" && handleMatchedFilterChange(matchedFilter === true ? null : true)}
                            />
                          </Tooltip>
                        )}
                        {testRun.failed_count > 0 && (
                          <Tooltip title={`Failed: ${testRun.failed_count} (click to filter)`}>
                            <div
                              role="button"
                              tabIndex={0}
                              className="bg-red-500 h-full transition-all cursor-pointer hover:opacity-90"
                              style={{
                                width: `${(testRun.failed_count / testRun.item_count) * 100}%`,
                                opacity: matchedFilter === null || matchedFilter === false ? 1 : 0.5,
                                boxShadow: matchedFilter === false ? "0 0 0 2px #1890ff" : "none",
                              }}
                              onClick={() => handleMatchedFilterChange(matchedFilter === false ? null : false)}
                              onKeyDown={(e) => e.key === "Enter" && handleMatchedFilterChange(matchedFilter === false ? null : false)}
                            />
                          </Tooltip>
                        )}
                        {testRun.error_count > 0 && (
                          <Tooltip title={`Error: ${testRun.error_count} (click to filter)`}>
                            <div
                              role="button"
                              tabIndex={0}
                              className="bg-orange-500 h-full transition-all cursor-pointer hover:opacity-90"
                              style={{
                                width: `${(testRun.error_count / testRun.item_count) * 100}%`,
                                opacity: matchedFilter === null || matchedFilter === false ? 1 : 0.5,
                                boxShadow: matchedFilter === false ? "0 0 0 2px #1890ff" : "none",
                              }}
                              onClick={() => handleMatchedFilterChange(matchedFilter === false ? null : false)}
                              onKeyDown={(e) => e.key === "Enter" && handleMatchedFilterChange(matchedFilter === false ? null : false)}
                            />
                          </Tooltip>
                        )}
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>Passed</span>
                        <span>Failed</span>
                      </div>
                    </>
                  ) : (
                    <span className="text-sm text-muted-foreground font-gc-reg">No data available</span>
                  )}
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>

        {/* Items Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <h2 className="text-xl font-gc-med text-foreground mb-6">Items</h2>
            <WorkflowTestRunItemsTable
              items={items}
              loading={isItemsLoading}
              currentPage={itemsPage}
              totalPages={Math.ceil(totalItems / ITEMS_PER_PAGE)}
              totalItems={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setItemsPage}
              onCompareClick={handleCompare}
              onRowClick={(item) => item.status === "COMPLETED" && handleCompare(item)}
              matchedFilter={matchedFilter}
              onMatchedFilterChange={handleMatchedFilterChange}
            />
          </CardContent>
        </Card>

        <WorkflowTestRunItemComparison
          item={comparisonItem}
          isVisible={isComparisonVisible}
          onClose={() => {
            setIsComparisonVisible(false);
            setComparisonItem(null);
          }}
        />
      </div>
    </div>
  );
};

export default WorkflowTestRunDetail;
